import React, { useState } from "react";

import { Editor } from "@monaco-editor/react";
import { Check, FileJson } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface JsonEditorProps {
  json: object | string;
  onChanged: (json: object) => void;
  text?: string;
}

export default function JsonEditor({ json, onChanged, text }: JsonEditorProps) {
  const [open, setOpen] = useState(false);
  const [jsonValue, setJsonValue] = useState(() => {
    return typeof json === "string" ? json : JSON.stringify(json, null, 2);
  });
  const [error, setError] = useState<string | null>(null);
  const editorRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (json)
      setJsonValue(
        typeof json === "string" ? json : JSON.stringify(json, null, 2),
      );
  }, [json]);

  const handleJsonChange = (value?: string) => {
    if (!value) {
      setError("JSON is empty");
      return;
    }
    setJsonValue(value);
    let parsedJson;
    // Validate JSON
    try {
      parsedJson = JSON.parse(value);
      setError(null);
    } catch (e) {
      setError("Invalid JSON format");
    }
  };

  const handleSave = () => {
    if (!error && jsonValue.trim()) {
      onChanged(JSON.parse(jsonValue));
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="space-x-2" type="button">
          <FileJson className="h-4 w-4" />
          {text ?? "Edit JSON"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-none w-screen h-screen m-0 rounded-none">
        <DialogHeader>
          <DialogTitle>JSON Editor</DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col gap-4">
          <div className="flex-1 relative">
            <Editor
              height={600}
              defaultLanguage="json"
              theme="vs-dark"
              defaultValue={jsonValue}
              onMount={(e) => {
                editorRef.current = e;
              }}
              onChange={handleJsonChange}
              className="rounded-sm"
            />
            {/* <Textarea
              value={jsonValue}
              onChange={(e) => handleJsonChange(e.target.value)}
              className="h-full resize-none font-mono text-sm"
              placeholder="Enter your JSON here..."
            /> */}
            {error && (
              <div className="absolute bottom-2 left-2 text-sm text-red-500 bg-red-50 px-2 py-1 rounded">
                {error}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="h-min">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!!error || !jsonValue.trim()}
            className="gap-2"
          >
            <Check className="h-4 w-4" />
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
